// bd-deepcontext-b4zw — the "Domain Filters (optional)" block plus the TLD tag
// input. This markup was duplicated verbatim in the create and edit forms inside
// ScheduledJobs.tsx; it now exists once.
import { ListBulletIcon } from "@heroicons/react/24/outline";
import React from "react";
import type { SelectedDomainInfo } from "../SiteListModal";
import TagPill from "../ui/TagPill";
import {
  addTld,
  dropLastTld,
  normalizeTld,
  removeTldAt,
} from "./domainExtensions";

interface DomainFiltersFieldProps {
  selectedDomainInfos: SelectedDomainInfo[];
  onOpenSiteListModal: () => void;
  domainExtensions: string[];
  onDomainExtensionsChange: (domainExtensions: string[]) => void;
}

const DomainFiltersField: React.FC<DomainFiltersFieldProps> = ({
  selectedDomainInfos,
  onOpenSiteListModal,
  domainExtensions,
  onDomainExtensionsChange,
}) => (
  <div>
    <label className="block text-sm font-medium text-ink mb-1">
      Domain Filters (optional)
    </label>
    {selectedDomainInfos.length > 0 ? (
      <div className="p-2 border border-line rounded-lg bg-[var(--ghost-bg)]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-ink">
            {selectedDomainInfos.length} domain
            {selectedDomainInfos.length !== 1 ? "s" : ""} selected
          </span>
          <button
            type="button"
            onClick={onOpenSiteListModal}
            className="text-accent hover:text-accent-active text-sm"
          >
            Change
          </button>
        </div>
        <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
          {selectedDomainInfos.slice(0, 10).map((info, idx) => (
            <span
              key={idx}
              className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                info.listType === "include"
                  ? "bg-safe-bg text-safe"
                  : "bg-risk-bg text-risk"
              }`}
            >
              {info.domain}
            </span>
          ))}
          {selectedDomainInfos.length > 10 && (
            <span className="px-2 py-0.5 text-xs text-ink-muted">
              +{selectedDomainInfos.length - 10} more
            </span>
          )}
        </div>
      </div>
    ) : (
      <button
        type="button"
        onClick={onOpenSiteListModal}
        className="w-full p-2 border-2 border-dashed border-line rounded-lg hover:border-accent hover:bg-[var(--nav-active-bg)] transition-colors flex items-center justify-center gap-2 text-ink-muted"
      >
        <ListBulletIcon className="h-5 w-5" />
        <span>Select Domains</span>
      </button>
    )}
    <p className="text-xs text-ink-muted mt-1">
      Filter URLs by domain whitelist/blacklist
    </p>

    {/* Domain Extensions (TLD Filter) */}
    <div className="mt-3">
      <label className="block text-xs font-medium text-ink-muted mb-1">
        Domain Extensions (TLD Filter)
      </label>
      <div className="flex flex-wrap gap-1.5 p-2 border border-line rounded-lg focus-within:ring-2 focus-within:ring-accent focus-within:border-accent min-h-[42px] bg-surface">
        {domainExtensions.map((ext, idx) => (
          <TagPill
            key={idx}
            onRemove={() => onDomainExtensionsChange(removeTldAt(domainExtensions, idx))}
            removeLabel={`Remove .${ext}`}
          >
            .{ext}
          </TagPill>
        ))}
        <input
          type="text"
          placeholder={domainExtensions.length === 0 ? "Type TLD and press Enter (e.g., sk)" : ""}
          className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
          onKeyDown={(e) => {
            const input = e.currentTarget;
            const value = normalizeTld(input.value);

            if ((e.key === "Enter" || e.key === ",") && value) {
              e.preventDefault();
              onDomainExtensionsChange(addTld(domainExtensions, value));
              input.value = "";
            } else if (e.key === "Backspace" && !input.value && domainExtensions.length > 0) {
              // Remove last tag on backspace if input is empty
              onDomainExtensionsChange(dropLastTld(domainExtensions));
            }
          }}
          onBlur={(e) => {
            const next = addTld(domainExtensions, e.currentTarget.value);
            if (next !== domainExtensions) {
              onDomainExtensionsChange(next);
              e.currentTarget.value = "";
            }
          }}
        />
      </div>
      <p className="text-xs text-ink-muted mt-1">
        Only scan websites ending in these TLDs. Press Enter to add. Leave empty to scan all.
      </p>
    </div>
  </div>
);

export default DomainFiltersField;
